import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('click', this.#handleExternalTriggerClick, true);
    this.refs.dialog.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleExternalTriggerClick, true);
    this.refs.dialog.removeEventListener('close', this.#handleDialogClose);
  }

  #handleDialogClose = () => {
    document.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());

    requestAnimationFrame(() => {
      document.documentElement.removeAttribute('scroll-lock');
      document.documentElement.style.removeProperty('overflow');
      document.body.classList.remove('overflow-hidden');
      document.body.style.removeProperty('overflow');
    });
  };

  /** @param {Event & { detail?: { data?: { source?: string } } }} event */
  #handleCartAdd = (event) => {
    if (event?.detail?.data?.source === 'bv-cart-progress') return;
    if (this.hasAttribute('auto-open')) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.showDialog();
        });
      });
    }
  };

  /** @param {MouseEvent} event */
  #handleExternalTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-cart-drawer-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    // Don't intercept triggers that are inside this component (they can use declarative events)
    if (this.contains(trigger)) return;

    event.preventDefault();
    this.open();
  };

  open() {
    this.showDialog();

    // dispatch a shared event so any listeners know the drawer is opening
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
